import { Expose, Type } from "class-transformer";
import {
    IsBoolean,
    IsDate,
    IsInt,
    IsObject,
    IsOptional,
    IsString,
    Max,
    MaxLength,
    Min,
    ValidateNested,
} from "class-validator";
import { DriveItemDTO } from '@haohan/drive-dto';

/** GameEngine */
export class GameEngineItemDTO {
    @Expose()
    @IsString()
    name: string;

    @Expose()
    @IsString()
    title: string;

    @Expose()
    @IsString()
    description: string;

    @Expose()
    @IsString()
    logo: string;
}

/** Game */

export class GameItemDTO {
    @Expose()
    @IsString()
    id: string;

    @Expose()
    @IsString()
    engineName: string;

    @Expose()
    @IsString()
    publicHash: string;

    @Expose()
    @IsString()
    name: string;

    @Expose()
    @IsOptional()
    @IsString()
    gameId: string;

    @Expose()
    @IsObject()
    config: {}

    @Expose()
    @IsBoolean()
    isAuthor: boolean;

    @Expose()
    @IsString()
    driveItemID: string;

    @Expose()
    @Type(() => Date)
	updatedAt: Date;

	@Expose()
    @Type(() => Date)
	createdAt: Date;
}

/** Create Game */

export class CreateGameInDTO {
    @IsString()
    engineName: string;

    @IsString()
    name: string;

    @IsObject()
    config: {}

    @IsString()
    folderID: string
}

export class CreateGameOutDTO {
    @IsObject()
    @Type(() => GameItemDTO)
    game: GameItemDTO;

    @IsString()
    driveItem: DriveItemDTO;
}

/** Update Game Settings */

export class UpdateGameSettingsInDTO {
    @IsString()
    publicHash: string;

    @IsString()
    name: string;

    @IsObject()
    config: {}
}

export class UpdateGameSettingsOutDTO {
    @IsObject()
    @Type(() => GameItemDTO)
    game: GameItemDTO;
}


export class ProcessActionInDTO {
    @IsObject()
    action: unknown;
}

export class GameSessionResultDTO {
    @IsInt()
    total: number;
    
    @IsInt()
    passed: number;

    @Type(() => GameSessionChallengeResultDTO)
    challenges: GameSessionChallengeResultDTO[]
}

export class ProcessActionOutDTO {
    @Type(() => GameSessionResultDTO)
    result: GameSessionResultDTO

    nextChallenge?: GameSessionChallengeDTO;

    correct?: boolean;

    state: {}
}

export class GetMyGamesQueryDTO {
	@IsOptional()
	@IsString()
	@Type(() => String)
	q?: string = "";
    
    @IsOptional()
	@IsInt()
	@Min(1)
	@Type(() => Number)
	page?: number = 1;

	@IsOptional()
	@IsInt()
	@Min(1)
	@Max(100)
	@Type(() => Number)
	limit?: number = 24;
}

export class GetAllGameEnginesOutDTO {
    @Type(() => GameEngineItemDTO)
    engines: GameEngineItemDTO[];

}

export class GetGameEngineOutDTO {
    @Type(() => GameEngineItemDTO)
    engine: GameEngineItemDTO;

}

export class GetMyGamesItemDTO {
    @Expose()
    @Type(() => GameItemDTO)
    game: GameItemDTO;

    @Expose()
    @Type(() => GameEngineItemDTO)
    engine: GameEngineItemDTO;

    @Expose()
    @Type(() => DriveItemDTO)
    driveItem: DriveItemDTO;
}

export class GetMyGamesOutDTO {
    @Type(() => GetMyGamesItemDTO)
    items: GetMyGamesItemDTO[];

    @IsInt()
    total: number;
}

/** Start random game */

export class StartRadomGameInDTO {
    @IsString()
    gameEngine: string;
}

export class GameSessionChallengeDTO {
    @IsString()
	challengeId: string;
}

export class GameSessionChallengeResultDTO {
    @IsInt()
    mistakes: number;
    @IsInt()
    correctActions: number;
    @IsBoolean()
    completed: boolean;
    @IsBoolean()
    failed: boolean;
    @IsInt()
    hints: number;
}

export class GameSessionDTO {
    @IsString()
    publicHash: string;

    @IsString()
    gamePublicHash: string;

    @IsString()
    title: string;

    @IsString()
    description: string;

    @IsString()
    gameEngine: string;

    @IsInt()
    gameVersion: number;

    @IsString()
    isAuthor: boolean;

    @IsObject()
    data: unknown;

    @IsObject()
    config: unknown;
}

export class GetNextChallengeOutDTO {
    @Type(() => GameSessionChallengeDTO)
    nextChallenge?: GameSessionChallengeDTO

    @Type(() => GameSessionResultDTO)
    result: GameSessionResultDTO

    state: {};
}

export class GetNextChallengeInDTO {
    @IsString()
    publicHash: string;
}

/**  getGameResults **/
export class GetGameResultsQueryDTO {
    @IsOptional()
	@IsInt()
	@Min(1)
	@Type(() => Number)
	page?: number = 1;

	@IsOptional()
	@IsInt()
	@Min(1)
	@Max(100)
	@Type(() => Number)
	limit?: number = 24;
}

export class GameResultScore {
    @IsInt()
    mistakes: number = 0;

    @IsOptional()
    @IsInt()
    correctActions: number = 0;

    @IsInt()
    hints: number = 0;
}

export class GetGameResultsPlayerOutDTO {
    @IsString()
    name: string
}

export class GetGameResultsItemOutDTO {
    @IsString()
    publicHash: string;

    @Type(() => GetGameResultsPlayerOutDTO)
    player: GetGameResultsPlayerOutDTO;

    @Type(() => GameResultScore)
    score: GameResultScore;
    
    @Type(() => Date)
    startDate: Date

    @Type(() => Date)
    finishDate: Date
}


export class GetGameResultsOutDTO {
    @IsInt()
    total: number

    @Type(() => GetGameResultsItemOutDTO)
    items: GetGameResultsItemOutDTO[]
}

/**
 * getGameResultDetails
 */


export class GetGameSessionResultDetailsOutDTO {
    @IsString()
    publicHash: string;

    @IsString()
    gameEngine: string;

    @IsString()
    gamePublicHash: string;

    @Type(() => GetGameResultsPlayerOutDTO)
    player: GetGameResultsPlayerOutDTO;

    @Type(() => GameResultScore)
    score: GameResultScore;

    @IsBoolean()
    isAuthor: boolean;

    @Type(() => GameSessionChallengeResultDTO)
    challenges: GameSessionChallengeResultDTO[]
    
    @Type(() => Date)
    startDate: Date

    @Type(() => Date)
    finishDate: Date
}

/** Start Game */

export class StartGameInPlayerDTO {
    @MaxLength(250)
    @IsString()
    name: string;
}

export class StartGameInDTO {
    @IsString()
    publicHash: string;

    @ValidateNested()
    @Type(() => StartGameInPlayerDTO)
    player: StartGameInPlayerDTO;
}

export class StartGameOutDTO {
    @Type(() => GameSessionDTO)
	gameSession: GameSessionDTO    
}
